import type { Save } from '../meta/save/schema';
import { SaveStore } from '../meta/save/store';
import { platformStorage } from '../platform/storage';
import type { EndlessMode } from '../gen/modes';
import { recordEndlessScore } from '../meta/economy';
import { startTracking } from '../meta/reveals';

/**
 * The game's single save store, plus small helpers for Daily/Endless records. Created lazily so
 * nothing touches storage until the game boots.
 */
let store: SaveStore | null = null;

export function saves(): SaveStore {
  if (!store) {
    const storage = platformStorage();
    store = new SaveStore(storage);
    if (store.loadResult.status === 'fresh') importPrototypeRecords(store);
    // Milestones and the Garage's NEW badge start from where the save is (an old save is not
    // buried in cheers for what it already has).
    const save = store.save;
    if (!save.milestones || !save.garageSeen) store.update(startTracking);
  }
  return store;
}

/** The early prototype kept bests in loose localStorage keys; fold them into the new save once. */
function importPrototypeRecords(s: SaveStore): void {
  try {
    const ls = window.localStorage;
    const endless = Number(ls.getItem('lm.endless.best'));
    const daily: Record<string, number> = {};
    for (let i = 0; i < ls.length; i++) {
      const key = ls.key(i);
      const m = key?.match(/^lm\.daily\.(\d{4}-\d{2}-\d{2})$/);
      const ticks = m && key ? Number(ls.getItem(key)) : NaN;
      if (m?.[1] && Number.isInteger(ticks) && ticks > 0) daily[m[1]] = ticks;
    }
    if (endless > 0 || Object.keys(daily).length > 0) {
      s.update((save: Save) => {
        save.endless.best = Number.isInteger(endless) && endless > 0 ? endless : 0;
        save.daily.bestTicks = daily;
      });
    }
  } catch {
    // Storage unavailable: nothing to import.
  }
}

/** Best score in an endless mode (Endless Lawns by default). */
export function endlessBest(mode: EndlessMode = 'lawns'): number {
  const e = saves().save.endless;
  return mode === 'lawns' ? e.best : (e.bests[mode] ?? 0);
}

/** Records an endless score; returns true if it is a new best for the mode. */
export function submitEndlessScore(score: number, mode: EndlessMode = 'lawns'): boolean {
  if (score <= endlessBest(mode)) return false;
  saves().update((s) => recordEndlessScore(s, mode, score));
  return true;
}

/** Best Daily clear time in ticks for a date, or null. */
export function dailyBestTicks(date: string): number | null {
  return saves().save.daily.bestTicks[date] ?? null;
}

/** Records a Daily clear (and the streak); returns true if it is a new best for that date. */
export function submitDailyTicks(date: string, ticks: number): boolean {
  const best = dailyBestTicks(date);
  saves().update((s) => {
    const streak = s.daily.streak;
    if (streak.lastDate !== date) {
      streak.count = streak.lastDate === previousDay(date) ? streak.count + 1 : 1;
      streak.lastDate = date;
    }
    if (best === null || ticks < best) s.daily.bestTicks[date] = ticks;
  });
  return best === null || ticks < best;
}

/** Today's date key in the player's local time zone. */
export function todayKey(now = new Date()): string {
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${m}-${d}`;
}

function previousDay(date: string): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return todayKey(new Date(y, m - 1, d - 1));
}

/** A fresh seed for an endless run. */
export function newRunSeed(): string {
  return `${Date.now().toString(36)}-${Math.floor(Math.random() * 1e9).toString(36)}`;
}
